import { IQuestion, Question, LocalizableString, PanelModel, PageModel } from 'survey-core';
import { SurveyPDF } from './survey';
import { IPoint, IRect, ISize, DocController } from './doc_controller';
import { FlatRepository } from './flat_layout/flat_repository';
import { IFlatQuestion } from './flat_layout/flat_question';
import { IPdfBrick } from './pdf_render/pdf_brick';
import { TextBrick } from './pdf_render/pdf_text';
import { ILinkOptions, LinkBrick } from './pdf_render/pdf_link';
import { HTMLBrick } from './pdf_render/pdf_html';
import { ImageBrick } from './pdf_render/pdf_image';
import { EmptyBrick } from './pdf_render/pdf_empty';
import { RowlineBrick } from './pdf_render/pdf_rowline';
import { CompositeBrick } from './pdf_render/pdf_composite';
import { ITextFieldBrickOptions, TextFieldBrick } from './pdf_render/pdf_textfield';
import { ISideValues, mergeRects, parseSideValues } from './utils';
import { getImageUtils } from './utils/image';
import { IAlignedTextStyle, IBorderStyle, IInputStyle, ITextStyle } from './style/types';

export interface IBorderDescription extends IRect, ISize {}

export enum BorderMode {
    Inside = 0,
    Middle = 1,
    Outside = 2,
}
export enum BorderRect {
    None = 0,
    Top = 1,
    Bottom = 2,
    Right = 4,
    Left = 8,
    All = 15
}
export interface IBorderExtendedStyle extends IBorderStyle {
    dashStyle?: { dashArray: [number, number] | [number], dashPhase: number };
    borderMode?: BorderMode;
}

export class SurveyHelper {
    public static readonly EPSILON: number = 2.2204460492503130808472633361816e-15;
    public static readonly HTML_TAIL_TEXT_SCALE: number = 0.24;

    public static parseWidth(width: string, maxWidth: number,
        columnsCount: number = 1, defaultUnit?: string): number {
        if (width.indexOf('calc') === 0) {
            return maxWidth / columnsCount;
        }
        const value: number = parseFloat(width);
        const unit: string = width.replace(/[^A-Za-z%]/g, '') || defaultUnit;
        let k: number;
        switch (unit) {
            case 'pt':
                k = 1.0;
                break;
            case 'mm':
                k = 72.0 / 25.4;
                break;
            case 'cm':
                k = 72.0 / 2.54;
                break;
            case 'in':
                k = 72.0;
                break;
            case 'px':
                k = 72.0 / 96.0;
                break;
            case 'pc':
                k = 12.0;
                break;
            case 'em':
                k = 12.0;
                break;
            case 'ex':
                k = 6.0;
                break;
            default:
            case '%':
                k = maxWidth / 100.0;
                break;
        }
        return Math.min(value * k, maxWidth);
    }
    public static pxToPt(value: number | string) {
        if (typeof value === 'string') {
            if(!isNaN(Number(value))) {
                value += 'px';
            }
            return SurveyHelper.parseWidth(value, Number.MAX_VALUE);
        }
        return value * 72.0 / 96.0;
    }
    public static parseColor(color: string) {
        let opacity: number;
        let match: RegExpMatchArray;
        if((color ?? '').match(/^rgba/)) {
            const matches = color.match(/[\d.]+/g);
            if(matches.length == 4) {
                color = `rgb(${matches[0]}, ${matches[1]}, ${matches[2]})`;
                opacity = parseFloat(matches[3]);
            }
        }
        else if((match = (color ?? '').match(/(#[A-Fa-f0-9]{6})([A-Fa-f0-9]{2})/))) {
            color = match[1];
            opacity = (Math.round(parseInt(match[2], 16) / 255 * 100)) / 100;
        }
        return { color, opacity };
    }
    public static mergeRects(...rects: IRect[]): IRect {
        return mergeRects(...rects);
    }
    public static createPoint(rect: IRect, isLeft: boolean = true, isTop: boolean = false): IPoint {
        return {
            xLeft: isLeft ? rect.xLeft : rect.xRight,
            yTop: isTop ? rect.yTop : rect.yBot
        };
    }
    public static createRect(point: IPoint, width: number, height: number): IRect {
        return {
            xLeft: point.xLeft,
            xRight: point.xLeft + width,
            yTop: point.yTop,
            yBot: point.yTop + height
        };
    }
    public static createHeaderRect(controller: DocController): IRect {
        return {
            xLeft: 0.0,
            xRight: controller.paperWidth,
            yTop: 0.0,
            yBot: controller.margins.top
        };
    }
    public static createFooterRect(controller: DocController): IRect {
        return {
            xLeft: 0.0,
            xRight: controller.paperWidth,
            yTop: controller.paperHeight - controller.margins.bot,
            yBot: controller.paperHeight
        };
    }
    public static chooseHtmlFont(controller: DocController, fontName?: string): string {
        return controller.useCustomFontInHtml ? fontName ?? controller.fontName : 'helvetica';
    }
    public static generateCssTextRule(style: ITextStyle): string {
        return `"font-size: ${style.fontSize}pt; font-weight: ${style.fontStyle}; font-family: ${style.fontName}; color: ${SurveyHelper.parseColor(style.fontColor).color}; lineHeight: ${style.lineHeight}; margin: 0"`;
    }
    public static createHtmlContainerBlock(html: string, controller: DocController, style?: Readonly<ITextStyle>): string {
        const newStyle: Required<ITextStyle> = SurveyHelper.getPatchedTextStyle(controller, style);
        const font = this.chooseHtmlFont(controller, newStyle.fontName);
        return `<div class="__surveypdf_html" style=${this.generateCssTextRule(newStyle)}>` +
            `<style>.__surveypdf_html p { margin: 0; line-height: ${newStyle.lineHeight}pt } body { margin: 0; }</style>${html}</div>`;
    }
    public static splitHtmlRect(controller: DocController, htmlBrick: IPdfBrick): IPdfBrick {
        const bricks: IPdfBrick[] = [];
        const htmlHeight: number = htmlBrick.height;
        const minHeight: number = controller.doc.getFontSize();
        htmlBrick.yBot = htmlBrick.yTop + minHeight;
        const emptyBrickCount: number = Math.floor(htmlHeight / minHeight) - 1;
        bricks.push(htmlBrick);
        const currPoint: IPoint = this.createPoint(htmlBrick);
        for (let i: number = 0; i < emptyBrickCount; i++) {
            bricks.push(new EmptyBrick(controller, this.createRect(currPoint, htmlBrick.width, minHeight)));
            currPoint.yTop += minHeight;
        }
        const remainingHeight: number = htmlHeight - (emptyBrickCount + 1) * minHeight;
        if (remainingHeight > 0) {
            bricks.push(new EmptyBrick(controller, this.createRect(currPoint, htmlBrick.width, remainingHeight)));
        }
        return new CompositeBrick(...bricks);
    }
    public static createPlainTextFlat(point: IPoint,
        controller: DocController, text: string, style: Required<IAlignedTextStyle>): CompositeBrick {
        controller.setTextStyle(style, true);
        const lines: string[] = controller.helperDoc.splitTextToSize(text,
            controller.paperWidth - controller.margins.right - point.xLeft);
        controller.restoreTextStyle(true);
        const currPoint: IPoint = this.clone(point);
        const composite: CompositeBrick = new CompositeBrick();
        lines.forEach((text: string) => {
            const size: ISize = controller.measureText(text, style);
            composite.addBrick(new TextBrick(controller,
                this.createRect(currPoint, size.width, size.height), { text }, style));
            currPoint.yTop += size.height;
        });
        if(style.textAlign == 'right') {
            const spaceXRight = point.xLeft + SurveyHelper.getPageAvailableWidth(controller);
            composite.translateX((xLeft, xRight) => {
                return { xLeft: xLeft + (spaceXRight - xRight), xRight: spaceXRight };
            });
        }
        if(style.textAlign == 'center') {
            const spaceXCenter = point.xLeft + SurveyHelper.getPageAvailableWidth(controller) / 2;
            composite.translateX((xLeft, xRight) => {
                const shift = spaceXCenter - (xLeft + xRight) / 2;
                return { xLeft: xLeft + shift, xRight: xRight + shift };
            });
        }
        return composite;
    }
    public static async createTextFlat(point: IPoint,
        controller: DocController, text: string | LocalizableString, style?: Readonly<IAlignedTextStyle>): Promise<IPdfBrick> {
        const newStyle: Required<IAlignedTextStyle> = { ... SurveyHelper.getPatchedTextStyle(controller, style), textAlign: style?.textAlign ?? 'left' };
        let result: IPdfBrick;
        if (typeof text === 'string' || !this.hasHtml(text)) {
            result = this.createPlainTextFlat(point, controller, typeof text === 'string' ?
                text : this.getLocString(<LocalizableString>text), newStyle);
        }
        else {
            result = this.splitHtmlRect(controller, await this.createHTMLFlat(point, controller,
                this.createHtmlContainerBlock(this.getLocString(text), controller, newStyle), newStyle));
        }
        return result;
    }
    public static mergeObjects(dest:any, ...sources:Array<any>):any {
        sources.forEach(source => {
            if(!source) return;
            Object.keys(source).forEach(key=>{
                if(typeof source[key] == 'object' && source[key] !== null && !Array.isArray(source[key])) {
                    dest[key] = SurveyHelper.mergeObjects(dest[key] ?? {}, source[key]);
                } else {
                    dest[key] = source[key];
                }
            });
        });
        return dest;
    }
    public static getPatchedTextStyle<T extends ITextStyle = ITextStyle>(controller: DocController, style?: Readonly<T>): T & Required<ITextStyle> {
        const newStyle = SurveyHelper.mergeObjects(SurveyHelper.getDefaultTextStyle(controller), style ?? {});
        if(style && style.lineHeight == undefined) {
            newStyle.lineHeight = newStyle.fontSize;
        }
        return newStyle;
    }
    public static getDefaultTextStyle(controller: DocController):Required<ITextStyle> {
        return {
            fontSize: controller.fontSize,
            fontName: controller.fontName,
            fontStyle: controller.fontStyle,
            lineHeight: controller.fontSize,
            fontColor: '#404040'
        };
    }
    public static hasHtml(text: LocalizableString): boolean {
        const pattern: RegExp = /<\/?[a-z][\s\S]*>/i;
        return text.hasHtml && (pattern.test((<any>text).renderedText) || pattern.test(text.renderedHtml));
    }
    private static getHtmlMargins(controller: DocController, point: IPoint): { top: number, bottom: number, width: number } {
        const width: number = controller.paperWidth - point.xLeft - controller.margins.right;
        return {
            top: controller.margins.top,
            bottom: controller.margins.bot,
            width: width > controller.unitWidth ? width : controller.unitWidth
        };
    }

    public static createHTMLRect(point: IPoint, controller: DocController,
        margins: { top: number, bottom: number, width: number }, resultY: number, style?: Readonly<ITextStyle>): IRect {
        const newStyle = this.getPatchedTextStyle(controller, style);
        const availablePageHeight: number = controller.paperHeight - controller.margins.bot - controller.margins.top;
        const height: number = (controller.helperDoc.getNumberOfPages() - 1) *
            (newStyle.fontSize * Math.floor(availablePageHeight / newStyle.fontSize))
            + resultY - margins.top + SurveyHelper.HTML_TAIL_TEXT_SCALE * newStyle.fontSize;
        const numberOfPages: number = controller.helperDoc.getNumberOfPages();
        controller.helperDoc.addPage();
        for (let i: number = 0; i < numberOfPages; i++) {
            controller.helperDoc.deletePage(1);
        }
        return SurveyHelper.createRect(point, margins.width, height);
    }

    public static async createHTMLFlat(point: IPoint, controller: DocController, html: string, style?: Readonly<ITextStyle>): Promise<IPdfBrick> {
        const margins: { top: number, bottom: number, width: number } = this.getHtmlMargins(controller, point);
        const newStyle = this.getPatchedTextStyle(controller, style);
        return await new Promise<IPdfBrick>((resolve) => {
            controller.helperDoc.fromHTML(html, point.xLeft, margins.top, {
                pagesplit: true, width: margins.width
            }, function (result: any) {
                const rect: IRect = SurveyHelper.createHTMLRect(point, controller, margins, result.y, newStyle);
                resolve(new HTMLBrick(controller, rect, { html }, newStyle));
            }, margins);
        });
    }
    public static generateFontFace(fontName: string, fontBase64: string, fontWeight: string) {
        return `@font-face { font-family: ${fontName}; ` +
            `src: url(data:application/font-woff;charset=utf-8;base64,${fontBase64}) format('woff'); ` +
            `font-weight: ${fontWeight}; }`;
    }
    public static generateFontFaceWithItalicStyle(fontName: string, fontBase64: string, fontWeight: string) {
        return `@font-face { font-family: ${fontName}; ` +
            `src: url(data:application/font-woff;charset=utf-8;base64,${fontBase64}) format('woff'); ` +
            `font-weight: ${fontWeight}; font-style: italic}`;
    }
    public static htmlToXml(html: string): string {
        const htmlDoc: Document = document.implementation.createHTMLDocument('');
        htmlDoc.write(html.replace(/\#/g, '%23'));
        htmlDoc.documentElement.setAttribute('xmlns', htmlDoc.documentElement.namespaceURI);
        htmlDoc.body.style.margin = 'unset';
        return (new XMLSerializer()).serializeToString(htmlDoc.body).replace(/%23/g, '#');
    }
    public static createSvgContent(html: string, width: number, controller: DocController) {
        const style: HTMLStyleElement = document.createElement('style');
        style.innerHTML = '.__surveypdf_html p { margin: unset; line-height: 22px; } body { margin: unset; }';
        document.body.appendChild(style);
        const div: HTMLDivElement = document.createElement('div');
        div.className = '__surveypdf_html';
        div.style.display = 'block';
        div.style.position = 'fixed';
        div.style.top = '-10000px';
        div.style.left = '-10000px';
        div.style.width = (width / 72.0 * 96.0) + 'px';
        div.style.boxSizing = 'initial';
        div.style.color = 'initial';
        div.style.fontFamily = 'initial';
        div.style.font = 'initial';
        div.style.lineHeight = 'initial';
        div.insertAdjacentHTML('beforeend', html);
        document.body.appendChild(div);
        const divWidth: number = div.offsetWidth;
        const divHeight: number = div.offsetHeight;
        div.remove();
        style.remove();
        let defs: string = '';
        if (controller.useCustomFontInHtml) {
            defs = `<defs><style>${this.generateFontFace(controller.fontName, controller.base64Normal, 'normal')}` +
                ` ${this.generateFontFace(controller.fontName, controller.base64Bold, 'bold')}</style></defs>`;
        } else {
            Object.keys(DocController.customFonts).forEach(fontName => {
                const font = DocController.customFonts[fontName];
                Object.keys(font).forEach((fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic') => {
                    if (fontStyle === 'normal' || fontStyle === 'bold') {
                        defs += `${this.generateFontFace(fontName, font[fontStyle], fontStyle)}`;
                    } else {
                        defs += `${this.generateFontFaceWithItalicStyle(fontName, font[fontStyle], fontStyle === 'italic' ? 'normal' : 'bold')}`;
                    }
                });
                defs = '<defs><style>' + defs + '</style></defs>';
            });
        }
        const svg: string = `<svg xmlns="http://www.w3.org/2000/svg" width="${divWidth}px" height="${divHeight}px">` + defs +
            '<style>.__surveypdf_html p { margin: unset; line-height: 22px; }</style>' +
            `<foreignObject width="${divWidth}px" height="${divHeight}px">` +
            this.htmlToXml(html) + '</foreignObject></svg>';
        return { svg, divWidth, divHeight };
    }
    private static setCanvas(controller: DocController, canvas: HTMLCanvasElement, divWidth: number, divHeight: number, img: HTMLImageElement): void {
        canvas.width = divWidth * controller.htmlToImageQuality;
        canvas.height = divHeight * controller.htmlToImageQuality;
        const context: CanvasRenderingContext2D = canvas.getContext('2d');
        context.scale(controller.htmlToImageQuality, controller.htmlToImageQuality);
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, divWidth, divHeight);
        context.drawImage(img, 0, 0);
    }
    public static async htmlToImage(html: string, width: number, controller: DocController): Promise<{ url: string, aspect: number }> {
        const { svg, divWidth, divHeight } = SurveyHelper.createSvgContent(html, width, controller);
        const data: string = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        const img: HTMLImageElement = new Image();
        img.crossOrigin='anonymous';
        img.src = data;
        return new Promise((resolve) => {
            img.onload = function () {
                const canvas: HTMLCanvasElement = document.createElement('canvas');
                SurveyHelper.setCanvas(controller, canvas, divWidth, divHeight, img);
                const url: string = canvas.toDataURL('image/jpeg', controller.htmlToImageQuality);
                canvas.remove();
                resolve({ url: url, aspect: divWidth / divHeight });
            };
            img.onerror = function () {
                resolve({ url: 'data:,', aspect: width / this.EPSILON });
            };
        });
    }
    public static getReadonlyRenderAs(question: Question, controller: DocController): 'auto' | 'text' | 'acroform' {
        return (<any>question).readonlyRenderAs === 'auto' ? controller.readonlyRenderAs : (<any>question).readonlyRenderAs;
    }
    public static async createCommentFlat(point: IPoint, controller: DocController, options: { rows?: number } & ITextFieldBrickOptions, style?: Readonly<IInputStyle>): Promise<IPdfBrick> {
        const newStyle: IInputStyle & Required<ITextStyle> = SurveyHelper.getPatchedTextStyle(controller, style);
        options.rows = options.rows ?? 1;
        options.value = options.value ?? '';
        const rect: IRect = this.createTextFieldRect(point, controller, options.rows, newStyle.lineHeight);
        let textFlat;
        if (options.shouldRenderReadOnly) {
            textFlat = await this.createReadOnlyTextFieldTextFlat(
                point, controller, options.value, newStyle);
        }
        const comment = new TextFieldBrick(controller, rect, options, newStyle);
        if(textFlat) {
            comment.textBrick = textFlat;
        }
        return comment;
    }

    public static get hasDocument(): boolean {
        return typeof document !== 'undefined';
    }
    public static async createImageFlat(point: IPoint, question: any,
        controller: DocController, options: { link: string, width: number, height: number, objectFit?: 'cover' | 'contain' | 'fill' }, applyImageFit?: boolean): Promise<IPdfBrick> {
        const imageUtils = getImageUtils();

        let imageInfo = await imageUtils.getImageInfo(options.link);
        if(applyImageFit ?? controller.applyImageFit) {
            imageInfo = await imageUtils.applyImageFit(imageInfo, options.objectFit || 'fill', options.width, options.height);
        }
        const rect = SurveyHelper.createRect(point, options.width, options.height);
        return new ImageBrick(controller, rect, { width: imageInfo.width, height: imageInfo.height, imageId: imageInfo.id, link: imageInfo.data });
    }
    public static createRowlineFlat(point: IPoint, controller: DocController,
        width?: number, color?: string): IPdfBrick {
        let xRight: number = typeof width === 'undefined' ?
            controller.paperWidth - controller.margins.right :
            point.xLeft + width;
        xRight = xRight > point.xLeft ? xRight : point.xLeft + this.EPSILON;
        return new RowlineBrick(controller, {
            xLeft: point.xLeft,
            xRight: xRight,
            yTop: point.yTop + this.EPSILON,
            yBot: point.yTop + this.EPSILON
        }, typeof color === 'undefined' ? null : color);
    }
    public static async createLinkFlat(point: IPoint, controller: DocController, options: ILinkOptions, style?: Readonly<ITextStyle>): Promise<IPdfBrick> {
        const newStyle: Required<ITextStyle> = SurveyHelper.getPatchedTextStyle(controller, style);
        const compositeText: CompositeBrick = <CompositeBrick>await this.
            createTextFlat(point, controller, options.text, newStyle);
        const compositeLink: CompositeBrick = new CompositeBrick();
        compositeText.unfold().forEach((text: TextBrick) => {
            compositeLink.addBrick(new LinkBrick(controller, text,
                {
                    link: options.link,
                    text: text.options.text,
                    readOnlyShowLink: options.readOnlyShowLink,
                    shouldRenderReadOnly: options.shouldRenderReadOnly
                },
                newStyle
            ));
            const linePoint: IPoint = this.createPoint(compositeLink);
            compositeLink.addBrick(this.createRowlineFlat(linePoint,
                controller, compositeLink.width, newStyle.fontColor));
        });
        return compositeLink;
    }
    public static createAcroformRect(rect: IRect): number[] {
        return [
            rect.xLeft,
            rect.yTop,
            rect.xRight - rect.xLeft,
            rect.yBot - rect.yTop
        ];
    }
    public static createTextFieldRect(point: IPoint, controller: DocController, lines: number = 1, lineHeight: number = controller.unitHeight): IRect {
        let width: number = controller.paperWidth - point.xLeft - controller.margins.right;
        width = Math.max(width, controller.unitWidth);
        const height: number = lineHeight * lines;
        return this.createRect(point, width, height);
    }
    public static async createReadOnlyTextFieldTextFlat(point: IPoint,
        controller: DocController, value: string, style: IInputStyle): Promise<IPdfBrick> {
        controller.pushMargins(point.xLeft, controller.margins.right);
        const textFlat: IPdfBrick = await this.createTextFlat(
            point, controller, value.toString(), style);
        controller.popMargins();
        return textFlat;
    }
    public static createRoundedShape(rect: IRect, style: { borderRadius?: number | Array<number> }, mergeAngles: boolean | ISideValues<boolean> = true): Map<keyof ISideValues, Array<Array<number>>> {
        const parsedMergeAngles: ISideValues<boolean> = typeof mergeAngles == 'object' ? mergeAngles : { top: mergeAngles, bot: mergeAngles, left: mergeAngles, right: mergeAngles };
        const parsedRadius = parseSideValues(style.borderRadius ?? 0);
        Object.keys(parsedRadius).forEach((side: keyof ISideValues) => {
            parsedRadius[side] = Math.max(0, Math.min(parsedRadius[side], (rect.xRight - rect.xLeft) / 2, (rect.yBot - rect.yTop) / 2));
        });
        function calcAngleLine(x: number, y: number, r: number, angle: number, rotAngle: number) {
            const l = 4/3 * Math.tan(angle / 4) * r;
            const base = [
                [r, 0],
                [r, l],
                [r * Math.cos(angle) + l * Math.sin(angle), r * Math.sin(angle) - l * Math.cos(angle)],
                [r * Math.cos(angle), r * Math.sin(angle)],
            ];
            return base.reduce((acc, point) => {
                acc.push(Math.cos(rotAngle) * point[0] - Math.sin(rotAngle) * point[1] + x);
                acc.push(Math.sin(rotAngle) * point[0] + Math.cos(rotAngle) * point[1] + y);
                return acc;
            }, []);
        }
        const lines: Map<keyof ISideValues, Array<Array<number>>> = new Map();
        lines.set('top', [[rect.xLeft + parsedRadius.top, rect.yTop, rect.xRight - parsedRadius.right, rect.yTop]]);
        lines.set('right', [[rect.xRight, rect.yTop + parsedRadius.right, rect.xRight, rect.yBot -parsedRadius.bot]]);
        lines.set('bot', [[rect.xRight - parsedRadius.bot, rect.yBot, rect.xLeft + parsedRadius.left, rect.yBot]]);
        lines.set('left', [[rect.xLeft, rect.yBot - parsedRadius.left, rect.xLeft, rect.yTop + parsedRadius.top]]);
        if(parsedRadius.top) {
            const angle = parsedMergeAngles.top ? Math.PI / 2 : Math.PI / 4;
            const r = parsedRadius.top;
            lines.get('left').push(calcAngleLine(rect.xLeft + r, rect.yTop + r, r, angle, Math.PI));
            if(!parsedMergeAngles.top) {
                lines.get('top').unshift(calcAngleLine(rect.xLeft + r, rect.yTop + r, r, angle, Math.PI + angle));
            }
        }
        if(parsedRadius.right) {
            const angle = parsedMergeAngles.right ? Math.PI / 2 : Math.PI / 4;
            const r = parsedRadius.right;
            lines.get('top').push(calcAngleLine(rect.xRight - r, rect.yTop + r, r, angle, 3 / 2 * Math.PI));
            if(!parsedMergeAngles.right) {
                lines.get('right').unshift(calcAngleLine(rect.xRight - r, rect.yTop + r, r, angle, 3 / 2 * Math.PI + angle));
            }
        }
        if(parsedRadius.bot) {
            const angle = parsedMergeAngles.bot ? Math.PI / 2: Math.PI / 4;
            const r = parsedRadius.bot;
            lines.get('right').push(calcAngleLine(rect.xRight - r, rect.yBot - r, r, angle, 0));
            if(!parsedMergeAngles.bot) {
                lines.get('bot').unshift(calcAngleLine(rect.xRight - r, rect.yBot - r, r, angle, angle));
            }
        }
        if(parsedRadius.left) {
            const angle = parsedMergeAngles.left ? Math.PI / 2 : Math.PI / 4;
            const r = parsedRadius.left;
            lines.get('bot').push(calcAngleLine(rect.xLeft + r, rect.yBot - r, r, angle, Math.PI / 2));
            if(!parsedMergeAngles.left) {
                lines.get('left').unshift(calcAngleLine(rect.xLeft + r, rect.yBot - r, r, angle, Math.PI / 2 + angle));
            }
        }
        return lines;
    }

    public static getDocLinesFromShape(lines: Map<keyof ISideValues, Array<Array<number>>>): { lines: Array<Array<number>>, point: Array<number> } {
        const startPoint = lines.get(lines.keys().next().value)[0].slice(0, 2);
        let currPoint = startPoint;
        const docLines: Array<Array<number>> = [];
        lines.forEach((value) => {
            const borderLines = value;
            return borderLines.forEach(line => {
                line = line.slice(2);
                docLines.push(line.map((v, i) => v - currPoint[i % 2]));
                currPoint = line.slice(line.length - 2);
            });
        });
        return { lines: docLines, point: startPoint };
    }

    public static renderFlatBorders(controller: DocController, options: IBorderDescription, style: IBorderExtendedStyle): void {
        const newStyle: Required<IBorderExtendedStyle> = SurveyHelper.mergeObjects({}, { borderMode: BorderMode.Inside }, style);
        const borderWidth = parseSideValues(style.borderWidth ?? 0);
        const borderColor = parseSideValues(style.borderColor);
        if([borderColor, borderWidth].some(map => Object.keys(map).every((k: keyof ISideValues) => !map[k]))) return;

        const scaleFactor = newStyle.borderMode == BorderMode.Middle ? 0 : (newStyle.borderMode == BorderMode.Inside ? 0.5 : -0.5);
        const scaledRect: IRect = {
            xLeft: options.xLeft + scaleFactor * borderWidth.left,
            yTop: options.yTop + scaleFactor * borderWidth.top,
            xRight: options.xRight - scaleFactor * borderWidth.right,
            yBot: options.yBot - scaleFactor * borderWidth.bot,
        };
        const mergeAngles: ISideValues<boolean> = {
            top: borderWidth.left == borderWidth.top && borderColor.left == borderColor.top,
            right: borderWidth.top == borderWidth.right && borderColor.top == borderColor.right,
            bot: borderWidth.right == borderWidth.bot && borderColor.right == borderColor.bot,
            left: borderWidth.bot == borderWidth.left && borderColor.bot == borderColor.left,
        };
        const lines = SurveyHelper.createRoundedShape(scaledRect, { ...newStyle }, mergeAngles);

        const continuousLines: Array<Map<keyof ISideValues, number[][]>> = [];
        let maxSequenceIndex = 0;
        let maxSequenceLength = 0;
        let currentSequenceIndex = 0;
        let currentSequenceLength = 0;
        let prevBorderWidth: number;
        let prevBorderColor: string;
        const keys = Array.from(lines.keys()) as Array<keyof ISideValues>;
        keys.concat(keys).forEach((key, index) => {
            const currentBorderWidth = borderWidth[key];
            const currentBorderColor = borderColor[key];
            if(index !== 0 && currentBorderWidth == prevBorderWidth && currentBorderColor == prevBorderColor) {
                currentSequenceLength++;
            } else {
                prevBorderWidth = currentBorderWidth;
                prevBorderColor = currentBorderColor;
                if(currentSequenceLength > maxSequenceLength) {
                    maxSequenceIndex = currentSequenceIndex;
                    maxSequenceLength = currentSequenceLength;
                }
                currentSequenceIndex = index;
                currentSequenceLength = 1;
            }
        });
        if(maxSequenceLength == 0) {
            maxSequenceLength = currentSequenceLength;
            maxSequenceIndex = currentSequenceIndex;
        }

        if(maxSequenceLength >= 2) {
            const maxContinuousLine: Map<keyof ISideValues, number[][]> = new Map();
            for(let i = maxSequenceIndex; i < maxSequenceIndex + maxSequenceLength; i ++) {
                const key = keys[i % keys.length];
                maxContinuousLine.set(key, lines.get(key));
            }
            continuousLines.push(maxContinuousLine);
            const leftKeys = keys.filter(key => !maxContinuousLine.has(key));
            if(leftKeys.length !== 0) {
                if(leftKeys.length == 2 && leftKeys[0] == 'top' && leftKeys[1] == 'left') leftKeys.reverse();
                if(leftKeys.every(key => borderWidth[leftKeys[0]] == borderWidth[key] && borderColor[leftKeys[0]] == borderColor[key])) {
                    const continuousLine: Map<keyof ISideValues, number[][]> = new Map();
                    leftKeys.forEach(key => { continuousLine.set(key, lines.get(key)); });
                    continuousLines.push(continuousLine);
                } else {
                    leftKeys.forEach(key => {
                        const map = new Map();
                        map.set(key, lines.get(key)),
                        continuousLines.push(map);
                    });
                }
            }
        } else {
            keys.forEach(key => {
                const map = new Map();
                map.set(key, lines.get(key)),
                continuousLines.push(map);
            });
        }
        if(newStyle.dashStyle) {
            const dashStyle = newStyle.dashStyle;
            const borderLength = (Math.abs(scaledRect.yTop - scaledRect.yBot) + Math.abs(scaledRect.xLeft - scaledRect.xRight)) * 2;
            const dashWithSpaceSize = dashStyle.dashArray[0] + (dashStyle.dashArray[1] ?? dashStyle.dashArray[0]);
            const dashSize = dashStyle.dashArray[0] + (borderLength % dashWithSpaceSize) / Math.floor(borderLength / dashWithSpaceSize);

            controller.doc.setLineDashPattern(
                [dashSize, dashStyle.dashArray[1] ?? dashStyle.dashArray[0]],
                dashStyle.dashPhase
            );
        }
        continuousLines.forEach(line => {
            const key = line.keys().next().value as keyof ISideValues;
            if(!borderColor[key] || !borderWidth[key]) return;
            controller.setDrawColor(borderColor[key]);
            controller.doc.setLineWidth(borderWidth[key]);
            const { lines: docLines, point } = this.getDocLinesFromShape(line);
            controller.doc.lines(docLines, ...point, [1, 1], 'S', line.size == 4);
            controller.restoreDrawColor();
        });
        if(newStyle.dashStyle) {
            controller.doc.setLineDashPattern([]);
        }
    }
    public static getLocString(text: LocalizableString): string {
        if (this.hasHtml(text)) return text.renderedHtml;
        return (<any>text).renderedText || text.renderedHtml;
    }
    public static getContentQuestion(question: Question): Question {
        return !!(<any>question).contentQuestion ? (<any>question).contentQuestion : question;
    }
    private static isBooleanDisplayMode(val: string): boolean {
        return val === 'radio' || val === 'checkbox' || val === 'switch';
    }
    private static isEmptyRenderAs(val: string): boolean {
        return !val || val === 'default';
    }
    private static getBooleanRenderAsValue(question: Question): string {
        if (!this.isEmptyRenderAs(question.renderAs)) return question.renderAs;
        const booleanQuestion = <any>question;
        const displayMode = booleanQuestion.displayMode;
        if (displayMode === 'custom' && booleanQuestion.customRenderAs) return booleanQuestion.customRenderAs;
        return this.isBooleanDisplayMode(displayMode) ? displayMode : 'default';
    }
    public static getContentQuestionTypeRenderAs(question: Question, survey: SurveyPDF): string {
        let renderAs = question.renderAs;
        if(question.getType() === 'boolean') {
            if(survey.options.useLegacyBooleanRendering) {
                renderAs = 'checkbox';
            } else {
                renderAs = this.getBooleanRenderAsValue(question);
            }
        }
        if(renderAs !== 'default') {
            const type = `${question.getType()}-${renderAs}`;
            if(FlatRepository.getInstance().isTypeRegistered(type)) return type;
        }
        return question.getType();
    }
    public static getContentQuestionType(question: Question, survey: SurveyPDF): string {
        if(!!question.customWidget) return question.customWidget.pdfQuestionType;
        return !!(<any>question).contentQuestion ? 'custom_model' : this.getContentQuestionTypeRenderAs(question, survey);
    }
    public static getPageAvailableWidth(controller: DocController): number {
        return controller.paperWidth - controller.margins.left - controller.margins.right;
    }
    public static getColumnWidth(controller: DocController, colCount: number, gapBetweenColumns: number) {
        return (this.getPageAvailableWidth(controller) - (colCount - 1) *
            gapBetweenColumns) / colCount;
    }
    public static setColumnMargins(controller: DocController, colCount: number, column: number, gapBetweenColumns: number) {
        const cellWidth: number = this.getColumnWidth(controller, colCount, gapBetweenColumns);
        controller.margins.left = controller.margins.left + column *
            (cellWidth + gapBetweenColumns);
        controller.margins.right = controller.margins.right + (colCount - column - 1) *
            (cellWidth + gapBetweenColumns);
    }
    public static moveRect(rect: IRect, left: number = rect.xLeft, top: number = rect.yTop): IRect {
        return {
            xLeft: left,
            yTop: top,
            xRight: left + rect.xRight - rect.xLeft,
            yBot: top + rect.yBot - rect.yTop
        };
    }
    public static createRectInsideBorders(rect: IRect, borderWidth: number | Array<number>): IRect {
        const parsedBorderWidth = parseSideValues(borderWidth);
        return {
            xLeft: rect.xLeft + parsedBorderWidth.left,
            yTop: rect.yTop + parsedBorderWidth.top,
            xRight: rect.xRight - parsedBorderWidth.right,
            yBot: rect.yBot - parsedBorderWidth.bot
        };
    }
    public static getFlatQuestion(survey: SurveyPDF, controller: DocController, question: Question) {
        const questionType: string = this.getContentQuestionType(question, survey);
        const style = survey.getElementStyle(question);
        const flatQuestion: IFlatQuestion = FlatRepository.getInstance().
            create(survey, question, controller, style, questionType);
        return flatQuestion;
    }
    public static async generateQuestionFlats(survey: SurveyPDF,
        controller: DocController, question: Question, point: IPoint): Promise<IPdfBrick[]> {
        const flatQuestion = SurveyHelper.getFlatQuestion(survey, controller, question);
        const questionFlats: IPdfBrick[] = await flatQuestion.generateFlats(point);
        return [...questionFlats];
    }
    public static getFlatPanel(survey: SurveyPDF,
        controller: DocController, panel: PanelModel) {
        return FlatRepository.getInstance().createPanel(survey, panel, controller, survey.getElementStyle(panel));
    }
    public static async generatePanelFlats(survey: SurveyPDF,
        controller: DocController, panel: PanelModel, point: IPoint): Promise<IPdfBrick[]> {
        return [...await this.getFlatPanel(survey, controller, panel).generateFlats(point)];
    }
    public static async generatePageFlats(survey: SurveyPDF,
        controller: DocController, page: PageModel, point: IPoint): Promise<IPdfBrick[]> {
        return [...await FlatRepository.getInstance().createPage(survey, page, controller, survey.getElementStyle(page)).generateFlats(point)];
    }
    public static isFontExist(controller: DocController, fontName: string): boolean {
        return controller.doc.internal.getFont(fontName).fontName === fontName;
    }
    public static isCustomFont(controller: DocController, fontName: string): boolean {
        return controller.doc.internal.getFont(fontName).encoding === 'Identity-H';
    }
    public static fixFont(controller: DocController): void {
        if (this.isCustomFont(controller, controller.fontName)) {
            controller.doc.text('load font', 0, 0);
            controller.doc.deletePage(1);
            controller.addPage();
        }
    }
    public static clone(src: any): any {
        const target: any = {};
        for (const prop in src) {
            if (src.hasOwnProperty(prop)) {
                target[prop] = src[prop];
            }
        }
        return target;
    }
    public static shouldRenderReadOnly(question: IQuestion, controller: DocController, readOnly?: boolean): boolean {
        return ((!!question && question.isReadOnly || readOnly) && SurveyHelper.getReadonlyRenderAs(
            <Question>question, controller) !== 'acroform') || controller?.compress;
    }
    public static isSizeEmpty(val: any): boolean {
        return !val || val === 'auto';
    }
    public static isHeightEmpty(val: any): boolean {
        return this.isSizeEmpty(val) || val == '100%';
    }
    public static async getCorrectedImageSize(controller: DocController, imageOptions: { imageWidth?: any, imageHeight?: any, imageLink: string, defaultImageWidth?: any, defaultImageHeight?: any }): Promise<ISize> {
        let { imageWidth, imageLink, imageHeight, defaultImageWidth, defaultImageHeight } = imageOptions;
        imageWidth = typeof imageWidth === 'number' ? imageWidth.toString() : imageWidth;
        imageHeight = typeof imageHeight === 'number' ? imageHeight.toString() : imageHeight;
        let widthPt: number = imageWidth && SurveyHelper.parseWidth(imageWidth, SurveyHelper.getPageAvailableWidth(controller), 1, 'px');
        let heightPt: number = imageHeight && SurveyHelper.parseWidth(imageHeight, SurveyHelper.getPageAvailableWidth(controller), 1, 'px');
        defaultImageWidth = typeof defaultImageWidth === 'number' ? defaultImageWidth.toString() : defaultImageWidth;
        defaultImageHeight = typeof defaultImageHeight === 'number' ? defaultImageHeight.toString() : defaultImageHeight;
        let defaultWidthPt: number = defaultImageWidth && SurveyHelper.parseWidth(defaultImageWidth, SurveyHelper.getPageAvailableWidth(controller), 1, 'px');
        let defaultHeightPt: number = defaultImageHeight && SurveyHelper.parseWidth(defaultImageHeight, SurveyHelper.getPageAvailableWidth(controller), 1, 'px');

        if(SurveyHelper.isSizeEmpty(imageWidth) || SurveyHelper.isHeightEmpty(imageHeight)) {
            const imageSize = await getImageUtils().getImageInfo(imageLink);
            if(!SurveyHelper.isSizeEmpty(imageWidth)) {
                if(imageSize && imageSize.width) {
                    heightPt = imageSize.height * widthPt / imageSize.width;
                }
            }
            else if(!SurveyHelper.isHeightEmpty(imageHeight)) {
                if(imageSize && imageSize.height) {
                    widthPt = imageSize.width * heightPt / imageSize.height;
                }
            }
            else if(imageSize && imageSize.height && imageSize.width) {
                heightPt = Math.min(imageSize.height, SurveyHelper.getPageAvailableWidth(controller));
                widthPt = Math.min(imageSize.width, SurveyHelper.getPageAvailableWidth(controller));
            }
        }
        return { width: widthPt || defaultWidthPt || 0, height: heightPt || defaultHeightPt || 0 };
    }
    public static alignVerticallyBricks(align: 'top' | 'center' | 'bottom', ...bricks: IPdfBrick[]) {
        const mergedRect = SurveyHelper.mergeRects(...bricks);
        bricks.forEach((brick) => {
            switch (align) {
                case 'center': {
                    brick.translateY((yTop, yBot) => {
                        const shift = ((mergedRect.yTop + mergedRect.yBot) - (yTop + yBot)) / 2;
                        return { yTop: yTop + shift, yBot: yBot + shift };
                    });
                    break;
                }
                case 'bottom': {
                    brick.translateY((yTop, yBot) => {
                        return { yTop: yTop + mergedRect.yBot - yBot, yBot: mergedRect.yBot };
                    });
                    break;
                }
                default: {
                    brick.translateY((yTop, yBot) => {
                        return { yTop: mergedRect.yTop, yBot: yBot - yTop + mergedRect.yTop };
                    });
                }
            }
        });
    }
    public static clear() {
        getImageUtils().clear();
    }
}